import { type SyntheticEvent, useMemo } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "ui/button";
import { Tab, Tabs } from "ui/tabs";
import { Breadcrumbs } from "ui/breadcrumbs";
import BackButton from "components/partials/back-button/back-button";
import TouchpointIcon from "containers/admin/clients/touchpoint/components/touchpoint-icon/touchpoint-icon";
import { ParamsType } from "containers/admin/clients/campaign/campaign-settings-page/campaign-settings-page";

import { useGetTouchpointVersionStatusesQuery } from "hooks/api/dictionary";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useTouchpointVersions } from "hooks/api/use-touchpoint-versions";

import type { UserAttributes } from "hooks/api/use-current-user";
import { useCurrentUser } from "hooks/api/use-current-user";
import { ClientAttributes, useCurrentClient } from "hooks/api/use-clients";
import { CampaignAttributes, useCurrentCampaign } from "hooks/api/use-campaigns";

import { replaceEndingToUrl } from "utilities";
import { hasPermission } from "utilities/user";
import { getTabViews } from "containers/admin/clients/touchpoint/components/touchpoint-page-header/touchpoint-page-header.helper";
import TouchpointPageHeaderMetadata from "containers/admin/clients/touchpoint/components/touchpoint-page-header/touchpoint-page-header-metadata";
import { useIsEmailWriterGenerating } from "containers/admin/clients/touchpoint/hooks/use-is-email-writer-generating";

import { TabItem, useTab } from "hooks/use-tab";

import { Permission } from "types/auth";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { TouchpointStatuses } from "types";
import { getCampaignPaths, getClientPaths } from "utilities/app-routes";

import Icons from "assets";

type StateBreadcrumb = { label: string; href: string };

type LocationState = {
  breadcrumbs?: StateBreadcrumb[];
};

interface BreadcrumbsType {
  client: ClientAttributes;
  campaign: CampaignAttributes;
  params?: ParamsType;
  currentUser: UserAttributes;
  locationState?: LocationState;
}

const getBreadcrumbsForCurrentPage = ({
  client,
  campaign,
  params,
  currentUser,
  locationState,
}: BreadcrumbsType) => {
  const isProgramTemplate = campaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const { clientCampaignsPath, clientProgramTemplatesPath } = getClientPaths({
    clientId: params?.clientId,
  });
  const { campaignCreativePath } = getCampaignPaths({
    clientId: params?.clientId,
    campaignId: params?.campaignId,
  });

  if (!hasPermission(currentUser, Permission.PERM_CLIENT_USER)) {
    // When navigated from a known page (e.g. Recent creatives, Approval queue batch),
    // use the breadcrumbs passed via location.state instead of the static campaign path.
    if (locationState?.breadcrumbs?.length) {
      return [{ label: client?.name, href: clientCampaignsPath }, ...locationState.breadcrumbs];
    }

    return isProgramTemplate
      ? [
          { label: client?.name, href: clientCampaignsPath },
          { label: "Creative templates", active: true, href: clientProgramTemplatesPath },
        ]
      : [
          { label: client?.name, href: clientCampaignsPath },
          { label: "Campaigns", href: clientCampaignsPath },
          { label: campaign?.name, href: campaignCreativePath },
        ];
  }

  return [
    { label: "Reviews", href: "/reviews" },
    { label: client?.name, active: true },
  ];
};

const TAB_VIEWS: readonly TabItem[] = [
  { name: "Preview", path: "preview" },
  { name: "Details", path: "details" },
  { name: "Version history", path: "versions" },
] as const;

export const TouchpointPageHeader = ({
  touchpointBatchId,
  selectedVersionFiscalYear,
}: {
  touchpointBatchId?: string;
  selectedVersionFiscalYear?: number;
}) => {
  const currentTouchpoint = useCurrentTouchpoint();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const params = useParams();
  const isEmailWriterGenerating = useIsEmailWriterGenerating();
  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();
  const { data: versions } = useTouchpointVersions({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });
  const options = getTabViews(
    !currentTouchpoint.validContentType,
    currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME,
    isEmailWriterGenerating,
  );

  const currentTab = useTab(TAB_VIEWS, "preview");
  const selectedTab = options.some(({ value }) => value === currentTab) ? currentTab : false;

  const isExternalPartnerTouchpoint = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const location = useLocation();

  const handleTabChange = (_event: SyntheticEvent, tabName: string) => {
    const tab = TAB_VIEWS.find((t) => t.name === tabName);
    if (tab) {
      navigate(`../${tab.path}`, { replace: true, state: location.state });
    }
  };
  const draftCount = useMemo(() => {
    const draftStatusNames = ["Draft", "System draft"];

    const draftStatuses = new Set(
      touchpointVersionStatuses
        .filter(({ versionStatusName }) => draftStatusNames.includes(versionStatusName))
        .map(({ status }) => status),
    );

    return (versions?.items ?? []).reduce((draftCount, item) => {
      if (item.status && draftStatuses.has(item.status as TouchpointStatuses))
        return draftCount + 1;
      return draftCount;
    }, 0);
  }, [touchpointVersionStatuses, versions?.items]);

  return (
    <div className="bg-neutrals-200 pt-3 mb-5">
      <div className="px-8">
        {isExternalPartnerTouchpoint ? (
          <div className="mt-0 mb-1.5 flex items-center">
            <BackButton show size="medium">Back</BackButton>
          </div>
        ) : (
          <Breadcrumbs
            sx={{ mb: 2 }}
            breadcrumbs={getBreadcrumbsForCurrentPage({
              client: currentClient,
              campaign: currentCampaign,
              params,
              currentUser,
              locationState: location.state as LocationState,
            })}
          />
        )}
        <div className="flex pb-2 justify-between items-center">
          <div className="flex items-center">
            <TouchpointIcon
              touchpointType={currentTouchpoint.type}
              className="size-6 shrink-0 text-gray-600 mr-2"
            />

            <span className="text-navy-800 font-bold text-xl" data-testid="touchpoint-name">
              {currentTouchpoint.name}
            </span>
          </div>

          <CopyURLButton />
        </div>
        <TouchpointPageHeaderMetadata
          touchpointBatchId={touchpointBatchId}
          draftCount={draftCount}
          isProgramTemplate={currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME}
          selectedVersionFiscalYear={selectedVersionFiscalYear}
        />
        {/* Hide toggle from CLIENT_USER */}
        {!hasPermission(currentUser, Permission.PERM_CLIENT_USER) && (
          <div className="mt-3">
            <Tabs
              appearance="bookmark"
              aria-label="touchpoint-tab-toggle"
              value={selectedTab}
              onChange={handleTabChange}>
              {options.map(({ label, value }) => (
                <Tab key={value} value={value} label={label} />
              ))}
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
};

const COPY_URL_BUTTON_SX: SxProps<Theme> = { mr: -2 };

const CopyURLButton = () => {
  const copyUrlToClipboard = () => {
    const currentTouchpointUrl = window.location.href;
    const urlToCopy = replaceEndingToUrl(currentTouchpointUrl, "preview");
    navigator.clipboard.writeText(urlToCopy);
  };

  return (
    <Button
      size="medium"
      variant="text"
      startIcon={<Icons.ContentCopy />}
      onClick={copyUrlToClipboard}
      sx={COPY_URL_BUTTON_SX}>
      Copy record URL
    </Button>
  );
};
